import cron from 'cron-validate';
import type { OptionPreset } from 'cron-validate/lib/types';
import cronstrue from 'cronstrue';
import 'cronstrue/locales/pt_BR';

import i18n, { t } from './i18n';

/**
 * Map an i18next language tag to a cronstrue locale id (see `cronstrue/locales`).
 * We side-import `pt_BR` only (~4k) and use it for any Portuguese UI locale.
 */
const mapAppLanguageToCronstrueLocale = (languageTag: string): string => {
  const normalized = languageTag.toLowerCase().replace('_', '-');
  if (normalized.startsWith('pt')) return 'pt_BR';
  return 'en';
};

const getCronstrueLocale = (): string =>
  mapAppLanguageToCronstrueLocale(
    i18n.resolvedLanguage || i18n.language || 'en-US'
  );

export const parseCronExpression = (
  cronExpression: string,
  languageTagForCronstrue?: string
): { isValidCron: boolean; humanReadableCron: string | null } => {
  try {
    const locale = languageTagForCronstrue
      ? mapAppLanguageToCronstrueLocale(languageTagForCronstrue)
      : getCronstrueLocale();
    const cronValue = cronstrue.toString(cronExpression, {
      locale
    });
    return {
      isValidCron: true,
      humanReadableCron: cronValue
    };
  } catch (e) {
    return {
      isValidCron: false,
      humanReadableCron: null
    };
  }
};

export type QuartzValidationResult =
  | { isValid: true; humanReadableCron: string | null }
  | { isValid: false; errors: string[] };

/**
 * Single Quartz preset for {@link https://www.npmjs.com/package/cron-validate cron-validate}.
 *
 * cron-validate cannot model an "optional year" field, so we use the 7-field
 * variant for everything and pad 6-field user input with a `*` year before
 * validating. That keeps a single source of truth and avoids the preset-cache
 * gotcha that comes with maintaining two presets.
 *
 * @see https://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html
 */
const QUARTZ_PRESET: OptionPreset = {
  presetId: 'quartz',
  useSeconds: true,
  useYears: true,
  useAliases: true,
  useBlankDay: true,
  allowOnlyOneBlankDayField: true,
  mustHaveBlankDayField: true,
  useLastDayOfMonth: true,
  useLastDayOfWeek: true,
  useNearestWeekday: true,
  useNthWeekdayOfMonth: true,
  seconds: { minValue: 0, maxValue: 59 },
  minutes: { minValue: 0, maxValue: 59 },
  hours: { minValue: 0, maxValue: 23 },
  daysOfMonth: { minValue: 1, maxValue: 31 },
  months: { minValue: 1, maxValue: 12 },
  daysOfWeek: { minValue: 1, maxValue: 7 },
  years: { minValue: 1970, maxValue: 2099 }
};

/**
 * cron-validate appends "(Input cron: '<expr>')" to every error message; that
 * trailer is redundant in the UI (the field already shows the value).
 */
const stripInputCronTrailer = (message: string): string =>
  message.replace(/\s*\(Input cron: '[^']*'\)\s*$/, '').trim();

/**
 * cron-validate emits structured English messages. Each pattern below maps
 * one of those templates onto an i18n key + named placeholders so the UI can
 * render the message in en-US or pt-BR without losing the field/value detail.
 *
 * Patterns are tried in order; the first match wins. Anything that doesn't
 * match falls through to the raw (English) message — that's our safety net
 * for new error templates introduced by future cron-validate versions.
 */
type ErrorPattern = {
  regex: RegExp;
  key: string;
  params: (m: RegExpMatchArray) => Record<string, string>;
};

const ERROR_PATTERNS: ErrorPattern[] = [
  {
    regex:
      /^Number '(.+?)' of (\w+) field is smaller than lower limit '(.+?)'\.?$/,
    key: 'below_lower',
    params: m => ({ number: m[1], field: m[2], limit: m[3] })
  },
  {
    regex:
      /^Number '(.+?)' of (\w+) field is bigger than upper limit '(.+?)'\.?$/,
    key: 'above_upper',
    params: m => ({ number: m[1], field: m[2], limit: m[3] })
  },
  {
    regex: /^Element '(.+?)' of (\w+) field is invalid\.?$/,
    key: 'invalid_element',
    params: m => ({ element: m[1], field: m[2] })
  },
  {
    regex: /^Element '(.+?)' of (\w+) field is not an integer\.?$/,
    key: 'not_integer',
    params: m => ({ element: m[1], field: m[2] })
  },
  {
    regex: /^Field (\w+) uses wildcard '\*', but is limited to (.+?)-(.+?)\.?$/,
    key: 'wildcard_limit',
    params: m => ({ field: m[1], lower: m[2], upper: m[3] })
  },
  {
    regex: /^One of the elements is empty in (\w+) field\.?$/,
    key: 'empty_element',
    params: m => ({ field: m[1] })
  },
  {
    regex: /^One of the range elements is empty in (\w+) field\.?$/,
    key: 'empty_range',
    params: m => ({ field: m[1] })
  },
  {
    regex: /^'\*' can't be part of a range in (\w+) field\.?$/,
    key: 'star_in_range',
    params: m => ({ field: m[1] })
  },
  {
    regex:
      /^Lower range end '(.+?)' is bigger than upper range end '(.+?)' of (\w+) field\.?$/,
    key: 'range_inverted',
    params: m => ({ lower: m[1], upper: m[2], field: m[3] })
  },
  {
    regex: /^List element '(.+?)' is not valid\. \(More than one '-'\)\.?$/,
    key: 'multiple_dashes',
    params: m => ({ element: m[1] })
  },
  {
    regex: /^List element '(.+?)' is not valid\. \(More than one '\/'\)\.?$/,
    key: 'multiple_slashes',
    params: m => ({ element: m[1] })
  },
  {
    regex: /^Stepping \('\/'\) is not allowed\.?$/,
    key: 'step_not_allowed',
    params: () => ({})
  },
  {
    regex:
      /^Second step element '(.*?)' of '(.+?)' is not valid \(doesnt exist\)\.?$/,
    key: 'step_missing',
    params: m => ({ element: m[1], listElement: m[2] })
  },
  {
    regex:
      /^Second step element '(.+?)' of '(.+?)' is not valid \(not a number\)\.?$/,
    key: 'step_not_number',
    params: m => ({ element: m[1], listElement: m[2] })
  },
  {
    regex: /^Second step element '(.+?)' of '(.+?)' cannot be zero\.?$/,
    key: 'step_zero',
    params: m => ({ element: m[1], listElement: m[2] })
  },
  {
    regex: /^Second step element '(.+?)' of '(.+?)' cannot be negative\.?$/,
    key: 'step_negative',
    params: m => ({ element: m[1], listElement: m[2] })
  },
  {
    regex: /^Second step element '(.+?)' of '(.+?)' is not an integer\.?$/,
    key: 'step_not_integer',
    params: m => ({ element: m[1], listElement: m[2] })
  },
  {
    regex:
      /^Second step element '(.+?)' of '(.+?)' is bigger than the upper limit '(.+?)'\.?$/,
    key: 'step_above_upper',
    params: m => ({ element: m[1], listElement: m[2], limit: m[3] })
  },
  {
    regex: /^Step value '(.+?)' must be greater than 0\.?$/,
    key: 'step_must_be_positive',
    params: m => ({ element: m[1] })
  },
  {
    regex: /^Step value '(.+?)' is too large for the range '(.+?)-(.+?)'\.?$/,
    key: 'step_too_large',
    params: m => ({ element: m[1], rangeStart: m[2], rangeEnd: m[3] })
  },
  {
    regex: /^The 'W' must be preceded by a day\.?$/,
    key: 'w_needs_day',
    params: () => ({})
  },
  {
    regex: /^Unexpected number of '#' in (.+?), can only be used once\.?$/,
    key: 'hash_too_many',
    params: m => ({ element: m[1] })
  },
  {
    regex:
      /^Unexpected value following the '#' symbol, a positive number was expected but found (.+?)\.?$/,
    key: 'hash_invalid_occurrence',
    params: m => ({ occurrence: m[1] })
  },
  {
    regex:
      /^Number of occurrence of the day of the week cannot be greater than 5\.?$/,
    key: 'hash_max_occurrence',
    params: () => ({})
  },
  {
    regex: /^useBlankDay is not enabled, but is used in (\w+) field\.?$/,
    key: 'blank_disabled',
    params: m => ({ field: m[1] })
  },
  {
    regex: /^blank notation is not allowed in (\w+) field\.?$/,
    key: 'blank_not_allowed',
    params: m => ({ field: m[1] })
  },
  {
    regex:
      /^Cannot use blank value in daysOfMonth and daysOfWeek field when allowOnlyOneBlankDayField option is enabled\.?$/,
    key: 'blank_both',
    params: () => ({})
  },
  {
    regex:
      /^Cannot specify both daysOfMonth and daysOfWeek field when mustHaveBlankDayField option is enabled\.?$/,
    key: 'must_have_blank',
    params: () => ({})
  },
  {
    regex:
      /^Cannot specify last day of month with lists, or ranges \(symbols ,\/\)\.?$/,
    key: 'l_with_lists',
    params: () => ({})
  },
  {
    regex:
      /^Cannot specify nearest weekday with lists, steps or ranges \(symbols ,-\/\)\.?$/,
    key: 'w_with_lists',
    params: () => ({})
  },
  {
    regex: /^Expected (\d+) values, but got (\d+)\.?$/,
    key: 'field_count_mismatch',
    params: m => ({ expected: m[1], got: m[2] })
  }
];

const FIELD_KEYS = new Set([
  'seconds',
  'minutes',
  'hours',
  'daysOfMonth',
  'months',
  'daysOfWeek',
  'years'
]);

const translateField = (raw: string): string => {
  if (!FIELD_KEYS.has(raw)) return raw;
  return t(`scenes.globals.cron.fields.${raw}`, { defaultValue: raw });
};

/**
 * Translate a raw cron-validate error into the active i18n locale.
 * Falls back to the original English message when no pattern matches —
 * better to leak English than to swallow a real error.
 */
export const translateCronError = (rawMessage: string): string => {
  const stripped = stripInputCronTrailer(rawMessage);
  const matched = ERROR_PATTERNS.map(pattern => ({
    pattern,
    match: stripped.match(pattern.regex)
  })).find(entry => entry.match !== null);

  if (!matched || !matched.match) return stripped;

  const params = matched.pattern.params(matched.match);
  if (params.field) params.field = translateField(params.field);

  return t(`scenes.globals.cron.errors.${matched.pattern.key}`, params);
};

/**
 * Validate a Quartz-format cron expression (6 or 7 whitespace-separated
 * fields). Validation is delegated to the `cron-validate` library; raw
 * English errors are mapped through {@link translateCronError} so the UI
 * shows them in the user's locale.
 *
 * @example
 * validateQuartzCron('0 0 12 * * ?')
 * // => { isValid: true, humanReadableCron: 'At 12:00 PM' }
 *
 * validateQuartzCron('0 65 12 * * ?')
 * // => { isValid: false, errors: ["Number '65' of minutes field is bigger than upper limit '59'."] }
 */
export const validateQuartzCron = (
  expression: string,
  languageTagForCronstrue?: string
): QuartzValidationResult => {
  const trimmed = (expression ?? '').trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return {
      isValid: false,
      errors: [t('scenes.globals.cron.errors.required')]
    };
  }

  const tokenCount = trimmed.split(' ').length;
  if (tokenCount < 6 || tokenCount > 7) {
    return {
      isValid: false,
      errors: [
        t('scenes.globals.cron.errors.field_count', {
          got: String(tokenCount)
        })
      ]
    };
  }

  // cron-validate has no "optional year" mode, so normalize 6-field input by
  // appending '*' (any year). Semantically identical, structurally one preset.
  const normalized = tokenCount === 6 ? `${trimmed} *` : trimmed;

  const result = cron(normalized, { preset: QUARTZ_PRESET });
  if (result.isValid()) {
    const langTag =
      languageTagForCronstrue ??
      (i18n.resolvedLanguage || i18n.language || 'en-US');
    const { humanReadableCron } = parseCronExpression(trimmed, langTag);
    return { isValid: true, humanReadableCron };
  }

  return {
    isValid: false,
    errors: result.getError().map(translateCronError)
  };
};
